import React, { useState, useEffect } from 'react';
import { User, UserProfileData, Skill, Occupation, SkillExperience, OccupationExperience } from '../types';
import SkillSelector from './SkillSelector';
import OccupationSelector from './OccupationSelector';

interface ProfileFormProps {
  user: User;
  onComplete: () => void;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ user, onComplete }) => {
  const [step, setStep] = useState(1);
  const [profileData, setProfileData] = useState<UserProfileData>({
    first_name: '',
    last_name: '',
    first_name_kana: '',
    last_name_kana: '',
    phone_number: '',
    birth_date: '',
    gender: '',
    prefecture: '',
    current_status: '',
    availability: '',
    commute_time_preference: ''
  });
  const [skillExperiences, setSkillExperiences] = useState<SkillExperience[]>([]);
  const [occupationExperiences, setOccupationExperiences] = useState<OccupationExperience[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const genderOptions = [
    { value: 'male', label: '男性' },
    { value: 'female', label: '女性' },
    { value: 'undisclosed', label: '回答しない' }
  ];

  const prefectureOptions = [
    { value: 'hokkaido', label: '北海道' },
    { value: 'aomori', label: '青森県' },
    { value: 'iwate', label: '岩手県' },
    { value: 'miyagi', label: '宮城県' },
    { value: 'akita', label: '秋田県' },
    { value: 'yamagata', label: '山形県' },
    { value: 'fukushima', label: '福島県' },
    { value: 'ibaraki', label: '茨城県' },
    { value: 'tochigi', label: '栃木県' },
    { value: 'gunma', label: '群馬県' },
    { value: 'saitama', label: '埼玉県' },
    { value: 'chiba', label: '千葉県' },
    { value: 'tokyo', label: '東京都' },
    { value: 'kanagawa', label: '神奈川県' },
    { value: 'niigata', label: '新潟県' },
    { value: 'toyama', label: '富山県' },
    { value: 'ishikawa', label: '石川県' },
    { value: 'fukui', label: '福井県' },
    { value: 'yamanashi', label: '山梨県' },
    { value: 'nagano', label: '長野県' },
    { value: 'gifu', label: '岐阜県' },
    { value: 'shizuoka', label: '静岡県' },
    { value: 'aichi', label: '愛知県' },
    { value: 'mie', label: '三重県' },
    { value: 'shiga', label: '滋賀県' },
    { value: 'kyoto', label: '京都府' },
    { value: 'osaka', label: '大阪府' },
    { value: 'hyogo', label: '兵庫県' },
    { value: 'nara', label: '奈良県' },
    { value: 'wakayama', label: '和歌山県' },
    { value: 'tottori', label: '鳥取県' },
    { value: 'shimane', label: '島根県' },
    { value: 'okayama', label: '岡山県' },
    { value: 'hiroshima', label: '広島県' },
    { value: 'yamaguchi', label: '山口県' },
    { value: 'tokushima', label: '徳島県' },
    { value: 'kagawa', label: '香川県' },
    { value: 'ehime', label: '愛媛県' },
    { value: 'kochi', label: '高知県' },
    { value: 'fukuoka', label: '福岡県' },
    { value: 'saga', label: '佐賀県' },
    { value: 'nagasaki', label: '長崎県' },
    { value: 'kumamoto', label: '熊本県' },
    { value: 'oita', label: '大分県' },
    { value: 'miyazaki', label: '宮崎県' },
    { value: 'kagoshima', label: '鹿児島県' },
    { value: 'okinawa', label: '沖縄県' }
  ];

  const currentStatusOptions = [
    { value: 'full_time', label: '正社員' },
    { value: 'freelance', label: 'フリーランス' },
    { value: 'part_time', label: 'パートタイム' },
    { value: 'contract', label: '契約社員' },
    { value: 'student', label: '学生' }
  ];

  const availabilityOptions = [
    { value: 'immediate', label: '即日' },
    { value: 'within_one_month', label: '1ヶ月以内' },
    { value: 'within_three_months', label: '3ヶ月以内' },
    { value: 'within_six_months', label: '6ヶ月以内' },
    { value: 'within_one_year', label: '1年以内' },
    { value: 'considering', label: '検討中' }
  ];

  const commuteTimeOptions = [
    { value: 'within_30_minutes', label: '30分以内' },
    { value: 'within_1_hour', label: '1時間以内' },
    { value: 'within_1_hour_30_minutes', label: '1時間30分以内' }
  ];

  const handleSubmit = async () => {
    setIsLoading(true);
    setErrors([]);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      
      const response = await fetch('/api/v1/user_profiles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          user_profile: profileData,
          skill_experiences: skillExperiences,
          occupation_experiences: occupationExperiences
        })
      });

      if (response.ok) {
        onComplete();
      } else {
        const errorData = await response.json();
        setErrors(errorData.errors || ['プロフィール作成に失敗しました']);
      }
    } catch (error) {
      setErrors(['ネットワークエラーが発生しました']);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div>
      <h4>基本情報</h4>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">姓 <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control"
            value={profileData.last_name}
            onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
            required
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">名 <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control"
            value={profileData.first_name}
            onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
            required
          />
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">姓（ひらがな） <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control"
            value={profileData.last_name_kana}
            onChange={(e) => setProfileData({...profileData, last_name_kana: e.target.value})}
            required
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">名（ひらがな） <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control"
            value={profileData.first_name_kana}
            onChange={(e) => setProfileData({...profileData, first_name_kana: e.target.value})}
            required
          />
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">電話番号 <span className="text-danger">*</span></label>
        <input
          type="tel"
          className="form-control"
          value={profileData.phone_number}
          onChange={(e) => setProfileData({...profileData, phone_number: e.target.value})}
          required
        />
      </div>

      <div className="mb-3">
        <label className="form-label">生年月日 <span className="text-danger">*</span></label>
        <input
          type="date"
          className="form-control"
          value={profileData.birth_date}
          onChange={(e) => setProfileData({...profileData, birth_date: e.target.value})}
          required
        />
      </div>
      
      <div className="mb-3">
        <label className="form-label">性別 <span className="text-danger">*</span></label>
        <div className="btn-group w-100" role="group">
          {genderOptions.map(option => (
            <button
              key={option.value}
              type="button"
              className={`btn ${profileData.gender === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setProfileData({...profileData, gender: option.value})}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">都道府県 <span className="text-danger">*</span></label>
        <select
          className="form-select"
          value={profileData.prefecture}
          onChange={(e) => setProfileData({...profileData, prefecture: e.target.value})}
          required
        >
          <option value="">選択してください</option>
          {prefectureOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h4>スキル情報</h4>
      <SkillSelector
        experiences={skillExperiences}
        onChange={setSkillExperiences}
      />
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h4>職種情報</h4>
      <OccupationSelector
        experiences={occupationExperiences}
        onChange={setOccupationExperiences}
      />
    </div>
  );

  const renderStep4 = () => (
    <div>
      <h4>希望条件</h4>
      <div className="mb-3">
        <label className="form-label">現在の状況 <span className="text-danger">*</span></label>
        <div className="d-grid gap-2">
          {currentStatusOptions.map(option => (
            <button
              key={option.value}
              type="button"
              className={`btn ${profileData.current_status === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setProfileData({...profileData, current_status: option.value})}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">案件可能時期 <span className="text-danger">*</span></label>
        <div className="d-grid gap-2">
          {availabilityOptions.map(option => (
            <button
              key={option.value}
              type="button"
              className={`btn ${profileData.availability === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setProfileData({...profileData, availability: option.value})}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">希望通勤時間</label>
        <div className="d-grid gap-2">
          {commuteTimeOptions.map(option => (
            <button
              key={option.value}
              type="button"
              className={`btn ${profileData.commute_time_preference === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setProfileData({...profileData, commute_time_preference: option.value})}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {errors.length > 0 && (
        <div className="alert alert-danger">
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}

      <div className="mb-4">
        <div className="progress">
          <div 
            className="progress-bar" 
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>
        <small className="text-muted">ステップ {step} / 4</small>
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}

      <div className="d-flex justify-content-between mt-4">
        {step > 1 && (
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => setStep(step - 1)}
          >
            戻る
          </button>
        )}
        
        {step < 4 ? (
          <button 
            type="button" 
            className="btn btn-primary ms-auto"
            onClick={() => setStep(step + 1)}
          >
            次へ
          </button>
        ) : (
          <button 
            type="button" 
            className="btn btn-success ms-auto"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? '登録中...' : '登録完了'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileForm;
